import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/authContext';
import { categoryAPI, locationAPI } from '../utils/api/newsAPI';
import { API_BASE_URL } from '../utils/api/config';
import { DEFAULT_CATEGORIES, DEFAULT_DISTRICTS, DISTRICT_BLOCKS, DISTRICT_VILLAGES } from '../utils/constants';
import './AdminNewsForm.css';

/**
 * AdminNewsForm Component
 * Reusable form for adding/editing news
 * Props: mode ('add' or 'edit'), initialData (object for edit mode), onSuccess (callback)
 */
function AdminNewsForm({ mode = 'add', initialData = null, onSuccess = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    description: '',
    image: '',
    category: '',
    district: '',
    block: '',
    village: '',
    tags: '',
    isBreaking: false,
    isPublished: false,
    videoUrl: ''
  });

  const loadInitialData = useCallback(async () => {
    try {
      const [categoriesData, districtsData] = await Promise.all([
        categoryAPI.getCategories(),
        locationAPI.getDistricts()
      ]);

      // Use API data if available, otherwise use defaults
      if (categoriesData.success && categoriesData.data.length > 0) {
        setCategories(categoriesData.data);
      } else {
        // Create default categories from constants
        const defaultCats = DEFAULT_CATEGORIES.map((name, index) => ({
          _id: name.toLowerCase().replace(/\s+/g, '-'),
          name: name,
          slug: name.toLowerCase().replace(/\s+/g, '-'),
          icon: '📰'
        }));
        setCategories(defaultCats);
      }

      if (districtsData.success && districtsData.data.length > 0) {
        setDistricts(districtsData.data);
      } else {
        // Create default districts from constants
        const defaultDist = DEFAULT_DISTRICTS.map((name, index) => ({
          _id: name.toLowerCase().replace(/\s+/g, '-'),
          name: name,
          slug: name.toLowerCase().replace(/\s+/g, '-')
        }));
        setDistricts(defaultDist);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      
      // Fallback to defaults on error
      const defaultCats = DEFAULT_CATEGORIES.map((name, index) => ({
        _id: name.toLowerCase().replace(/\s+/g, '-'),
        name: name,
        slug: name.toLowerCase().replace(/\s+/g, '-'),
        icon: '📰'
      }));
      setCategories(defaultCats);

      const defaultDist = DEFAULT_DISTRICTS.map((name, index) => ({
        _id: name.toLowerCase().replace(/\s+/g, '-'),
        name: name,
        slug: name.toLowerCase().replace(/\s+/g, '-')
      }));
      setDistricts(defaultDist);
    }
  }, []);

  const loadBlocks = useCallback(async (districtName) => {
    try {
      // Try to get blocks from constants first
      if (DISTRICT_BLOCKS[districtName]) {
        const blocksList = DISTRICT_BLOCKS[districtName].map((blockName, idx) => ({
          _id: `block-${idx}`,
          name: blockName
        }));
        setBlocks(blocksList);
        return;
      }

      // Fallback to API if not in constants
      const data = await locationAPI.getBlocks(districtName);
      if (data.success) setBlocks(data.data);
    } catch (error) {
      console.error('Error loading blocks:', error);
      // If both fail, try to get from constants one more time
      if (DISTRICT_BLOCKS[districtName]) {
        const blocksList = DISTRICT_BLOCKS[districtName].map((blockName, idx) => ({
          _id: `block-${idx}`,
          name: blockName
        }));
        setBlocks(blocksList);
      }
    }
  }, []);

  const loadVillages = useCallback(async (districtName, blockName) => {
    try {
      // Try to get villages from constants first
      if (DISTRICT_VILLAGES[districtName]?.[blockName]) {
        const villagesList = DISTRICT_VILLAGES[districtName][blockName].map((villageName, idx) => ({
          _id: `village-${idx}`,
          name: villageName
        }));
        setVillages(villagesList);
        return;
      }

      // Fallback to API if not in constants
      const data = await locationAPI.getVillages(blockName);
      if (data.success) setVillages(data.data);
    } catch (error) {
      console.error('Error loading villages:', error);
      // If both fail, try to get from constants one more time
      if (DISTRICT_VILLAGES[districtName]?.[blockName]) {
        const villagesList = DISTRICT_VILLAGES[districtName][blockName].map((villageName, idx) => ({
          _id: `village-${idx}`,
          name: villageName
        }));
        setVillages(villagesList);
      }
    }
  }, []);

  const populateFormData = useCallback(async (data) => {
    try {
      setFormData({
        title: data.title || '',
        shortDescription: data.shortDescription || '',
        description: data.description || '',
        image: data.image || '',
        category: data.category?._id || data.category || '',
        district: data.district || '',
        block: data.block || '',
        village: data.village || '',
        tags: data.tags?.join(', ') || '',
        isBreaking: data.isBreaking || false,
        isPublished: data.isPublished || false,
        videoUrl: data.videoUrl || ''
      });
      setImagePreview(data.thumbnail || data.image || '');

      // Load blocks and villages based on district
      if (data.district) {
        await loadBlocks(data.district);
        if (data.block) {
          await loadVillages(data.block);
        }
      }
    } catch (error) {
      console.error('Error populating form:', error);
    }
  }, [loadBlocks, loadVillages]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      populateFormData(initialData);
    }
  }, [initialData, mode, populateFormData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDistrictChange = async (e) => {
    const district = e.target.value;
    setFormData(prev => ({
      ...prev,
      district,
      block: '',
      village: ''
    }));
    setVillages([]);
    if (district) {
      await loadBlocks(district);
    }
  };

  const handleBlockChange = async (e) => {
    const block = e.target.value;
    setFormData(prev => ({
      ...prev,
      block,
      village: ''
    }));
    if (block && formData.district) {
      await loadVillages(formData.district, block);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Store the file object for FormData submission
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate category is selected and is a valid ObjectId or proper value
      if (!formData.category) {
        alert('कृपया श्रेणी चुनें');
        setLoading(false);
        return;
      }

      // Validate image is selected for add mode
      if (mode === 'add' && !imageFile) {
        alert('कृपया एक छवि चुनें');
        setLoading(false);
        return;
      }

      let response;
      if (mode === 'edit' && initialData?._id) {
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('shortDescription', formData.shortDescription);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('subCategory', formData.subCategory || '');
        formDataToSend.append('district', formData.district || '');
        formDataToSend.append('block', formData.block || '');
        formDataToSend.append('village', formData.village || '');
        formDataToSend.append('tags', formData.tags);
        formDataToSend.append('isBreaking', formData.isBreaking ? 'true' : 'false');
        formDataToSend.append('isPublished', formData.isPublished ? 'true' : 'false');
        formDataToSend.append('videoUrl', formData.videoUrl || '');
        formDataToSend.append('author', user?.id || '');

        if (imageFile) {
          formDataToSend.append('featuredImage', imageFile);
        }

        response = await fetch(`${API_BASE_URL}/news/${initialData._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: formDataToSend
        });
      } else {
        // For add: send as FormData (with file upload)
        const formDataToSend = new FormData();
        
        // Append all form fields
        formDataToSend.append('title', formData.title);
        formDataToSend.append('shortDescription', formData.shortDescription);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('subCategory', formData.subCategory || '');
        formDataToSend.append('isPublished', formData.isPublished);
        
        // Append tags - handle as array or string
        const tagsList = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        if (tagsList.length > 0) {
          // Send tags as string for backend to parse
          formDataToSend.append('tags', tagsList.join(','));
        }
        
        // Append video URL if provided
        if (formData.videoUrl) formDataToSend.append('videoUrl', formData.videoUrl);
        
        // Append the image file with field name 'featuredImage' (required by backend)
        if (imageFile) {
          formDataToSend.append('featuredImage', imageFile);
        }

        response = await fetch(`${API_BASE_URL}/news/add`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            // Don't set Content-Type header - browser will set it with boundary
          },
          body: formDataToSend
        });
      }

      const data = await response.json();
      if (data.success) {
        alert(mode === 'edit' ? 'समाचार अपडेट हो गया!' : 'समाचार जोड़ा गया!');
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/admin/news/add');
        }
      } else {
        alert('त्रुटि: ' + data.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('फॉर्म सबमिट करने में विफल');
    } finally {
      setLoading(false);
    }
  };

  if (loading && mode === 'edit') {
    return <div className="admin-news-form loading">लोड हो रहा है...</div>;
  }

  return (
    <form className="admin-news-form" onSubmit={handleSubmit}>
      <div className="form-container">
        {/* Title */}
        <div className="form-group">
          <label htmlFor="title">शीर्षक *</label>
          <input
            id="title"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            placeholder="समाचार का शीर्षक दर्ज करें"
          />
        </div>

        {/* Short Description */}
        <div className="form-group">
          <label htmlFor="shortDescription">संक्षिप्त विवरण *</label>
          <textarea
            id="shortDescription"
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleInputChange}
            required
            rows="2"
            placeholder="संक्षिप्त विवरण (500 शब्द तक)"
          />
        </div>

        {/* Full Description */}
        <div className="form-group fullwidth">
          <label htmlFor="description">पूर्ण विवरण *</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows="10"
            placeholder="पूरी खबर का विवरण दर्ज करें"
          />
        </div>

        {/* Image Upload */}
        <div className="form-group">
          <label htmlFor="image">छवि अपलोड करें *</label>
          <input
            id="image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            required={mode === 'add'}
          />
          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="Preview" />
            </div>
          )}
        </div>

        {/* Category */}
        <div className="form-group">
          <label htmlFor="category">श्रेणी *</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            required
          >
            <option value="">श्रेणी चुनें</option>
            {categories.map(cat => (
              <option key={cat._id} value={cat._id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* District */}
        <div className="form-group">
          <label htmlFor="district">जिला</label>
          <select
            id="district"
            name="district"
            value={formData.district}
            onChange={handleDistrictChange}
          >
            <option value="">जिला चुनें</option>
            {districts.map(dist => (
              <option key={dist._id} value={dist.name}>{dist.name}</option>
            ))}
          </select>
        </div>

        {/* Block */}
        {blocks.length > 0 && (
          <div className="form-group">
            <label htmlFor="block">ब्लॉक</label>
            <select
              id="block"
              name="block"
              value={formData.block}
              onChange={handleBlockChange}
            >
              <option value="">ब्लॉक चुनें</option>
              {blocks.map(block => (
                <option key={block._id} value={block.name}>{block.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Village */}
        {villages.length > 0 && (
          <div className="form-group">
            <label htmlFor="village">गांव</label>
            <select
              id="village"
              name="village"
              value={formData.village}
              onChange={handleInputChange}
            >
              <option value="">गांव चुनें</option>
              {villages.map(village => (
                <option key={village._id} value={village.name}>{village.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Tags */}
        <div className="form-group">
          <label htmlFor="tags">टैग (अल्पविराम से अलग करें)</label>
          <input
            id="tags"
            type="text"
            name="tags"
            value={formData.tags}
            onChange={handleInputChange}
            placeholder="टैग1, टैग2, टैग3"
          />
        </div>

        {/* Video URL */}
        <div className="form-group">
          <label htmlFor="videoUrl">वीडियो URL (YouTube)</label>
          <input
            id="videoUrl"
            type="url"
            name="videoUrl"
            value={formData.videoUrl}
            onChange={handleInputChange}
            placeholder="वीडियो URL (यदि कोई हो)"
          />
        </div>

        {/* Breaking News */}
        <div className="form-group checkbox">
          <label htmlFor="isBreaking">
            <input
              id="isBreaking"
              type="checkbox"
              name="isBreaking"
              checked={formData.isBreaking}
              onChange={handleInputChange}
            />
            ब्रेकिंग न्यूज़ के रूप में चिह्नित करें
          </label>
        </div>

        {/* Publish */}
        <div className="form-group checkbox">
          <label htmlFor="isPublished">
            <input
              id="isPublished"
              type="checkbox"
              name="isPublished"
              checked={formData.isPublished}
              onChange={handleInputChange}
            />
            तुरंत प्रकाशित करें
          </label>
        </div>
      </div>

      {/* Buttons */}
      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'सहेज रहे हैं...' : mode === 'edit' ? 'अपडेट करें' : 'जोड़ें'}
        </button>
        <button 
          type="button" 
          className="btn-secondary" 
          onClick={() => navigate('/admin/news/manage')}
          disabled={loading}
        >
          रद्द करें
        </button>
      </div>
    </form>
  );
}

export default AdminNewsForm;
